/**
 * CardElement - Iframe-based payment element
 * 
 * Mounts an iframe with the payment checkout form
 */

import { PaymentResult, PaymentResultStatus } from './react';
import { SDK } from './sdk';

import type { CSSProperties, Locale } from './types';
import { detectBrowserLocale } from './utils';

export interface CardElementOptions {
  /** Container element or CSS selector where the iframe will be mounted */
  container: string | HTMLElement;
  /** Payment ID for this transaction */
  paymentId: string;
  /** Callback when payment completes */
  onComplete?: (result: PaymentResult) => void;
  /** Callback when payment fails */
  onError?: (error: any) => void;
  /** Callback when user closes the payment */
  onClose?: () => void;
  /** Custom styling for the card element */
  style?: CSSProperties;
  /** Custom button text (default: "Complete Payment") */
  buttonText?: string;
  /** Custom placeholder text for inputs */
  placeholders?: {
    cardNumber?: string;
    expiry?: string;
    cvc?: string;
  };
  /** Show default success UI after payment (default true). If false, only unmount iframe. */
  showDefaultSuccessUI?: boolean;
}

interface InternalSDKConfig {
  publicKey: string;
  iframeUrl: string;
  timeout: number;
  debug: boolean;
  locale?: Locale;
}

export class CardElement {
  private sdk: SDK;
  private container: HTMLElement;
  private mounted: boolean = false;

  constructor(
    config: InternalSDKConfig,
    options: CardElementOptions
  ) {
    let containerElement: HTMLElement | null;
    if (typeof options.container === 'string') {
      containerElement = document.querySelector(options.container);
      if (!containerElement) {
        throw new Error(`Container not found: ${options.container}`);
      }
    } else {
      containerElement = options.container;
    }
    this.container = containerElement;

    this.sdk = new SDK({
      publicKey: config.publicKey,
      container: this.container,
      locale: config.locale ?? detectBrowserLocale(),
      config: {
        paymentId: options.paymentId,
        ...(options.style && { style: options.style }),
        ...(options.buttonText && { buttonText: options.buttonText }),
        ...(options.placeholders && { placeholders: options.placeholders }),
        ...(options.showDefaultSuccessUI !== undefined && {
          showDefaultSuccessUI: options.showDefaultSuccessUI,
        }),
      },
      onSuccess: (data) => {
        if (options.onComplete) {
          options.onComplete({
            status: PaymentResultStatus.SUCCESS,
            paymentId: options.paymentId,
          });
        }
        // Replace iframe with success UI (default, custom, or none)
        this.sdk.switchToSuccessState(data);
      },
      onError: (error) => {
        if (options.onError) {
          options.onError(error);
        } else if (options.onComplete) {
          options.onComplete({
            status: PaymentResultStatus.FAILED,
            paymentId: options.paymentId,
            error: error,
          });
        }
      },
      onClose: () => {
        if (options.onClose) {
          options.onClose();
        }
      },
    });
  }

  /**
   * Mount the CardElement to the DOM
   * This will create and display the iframe
   */
  mount(): void {
    if (this.mounted) {
      throw new Error('CardElement is already mounted');
    }

    this.sdk.init();
    this.mounted = true;
  }

  /**
   * Destroy the CardElement and cleanup
   */
  destroy(): void {
    if (this.mounted) {
      this.sdk.destroy();
      this.mounted = false;
    }
  }
}
